import { createContext, useContext, type ReactNode } from "react";
import { CloudOff, RotateCw, SearchX } from "lucide-react";
import { cn } from "@/utils/helpers";
import { KitButton } from "./kit/Button";

/*
 * Empty, loading and error (CreativeOS Library.lib.pen, Components / Status
 * and feedback, style B). No illustrations, no mascots: one boxed glyph, a
 * Barlow title saying what happened, one or two lines of why, and one way on.
 * Every state sits centred, across and down, in the space it stands in for
 * (Emad, 29 Sep). Page scale: 560 wide with a 32px title on the desk, 26px on
 * a phone. A read that fails hides its counts; it never shows zero.
 */
type Props = {
  minHeight?: number;
  /** "start": the state sits left-aligned at the top of its panel, as the
   *  Signals frames place it (their instance overrides the centring). The
   *  caller's className positions it. */
  align?: "center" | "start";
  className?: string;
};

const StartContext = createContext(false);

function Frame({ minHeight = 360, align = "center", className, children }: Props & { children: ReactNode }) {
  if (align === "start") {
    return (
      <StartContext.Provider value>
        <div className={cn("flex w-full max-w-[560px] flex-col items-start gap-3.5 text-start", className)}>
          {children}
        </div>
      </StartContext.Provider>
    );
  }
  return (
    <div
      className={cn("flex flex-col items-center justify-center px-5 py-15 text-center", className)}
      style={{ minHeight }}
    >
      <div className="flex w-full max-w-[560px] flex-col items-center gap-3.5">{children}</div>
    </div>
  );
}

function GlyphBox({ children }: { children: ReactNode }) {
  return (
    <div className="grid size-11 place-items-center rounded-card border border-co-line2 text-co-text2 [&_svg]:size-5">
      {children}
    </div>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <div className="font-section text-[26px] font-extrabold leading-[1.05] text-co-text md:text-[32px]">{children}</div>
  );
}

function Body({ children }: { children: ReactNode }) {
  return <div className="text-[15px] leading-[1.55] text-co-muted">{children}</div>;
}

function Actions({ children }: { children: ReactNode }) {
  const start = useContext(StartContext);
  return (
    <div className={cn("flex flex-wrap items-center gap-3", start ? "justify-start" : "justify-center")}>
      {children}
    </div>
  );
}

/** Loading names what is loading. Content that is fetched gets a skeleton shaped like it; this is for a whole view. */
export function Loading({ label = "Loading", minHeight }: { label?: string } & Props) {
  return (
    <Frame minHeight={minHeight}>
      <div
        role="status"
        aria-live="polite"
        className="flex items-center gap-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-co-muted"
      >
        <span aria-hidden className="co-livedot inline-block size-1.5 rounded-full bg-co-live" />
        {label}…
      </div>
    </Frame>
  );
}

/** Error names the problem and the recovery, then offers Try again. */
export function ErrorState({
  onRetry,
  minHeight,
  align,
  className,
  title = "This view did not load",
  body = "The agents are reachable but this request failed. Nothing you decided has changed.",
  icon,
  actions,
}: {
  /** Absent where trying again cannot help (a fixture, a harness): no button. */
  onRetry?: () => void;
  title?: string;
  /** null for no line under the title. */
  body?: ReactNode;
  /** A 20px glyph for the box; cloud-off by default. */
  icon?: ReactNode;
  /** Extra actions after Try again. */
  actions?: ReactNode;
} & Props) {
  return (
    <Frame minHeight={minHeight} align={align} className={className}>
      <GlyphBox>{icon ?? <CloudOff aria-hidden />}</GlyphBox>
      <Title>{title}</Title>
      {body ? <Body>{body}</Body> : null}
      <Actions>
        {onRetry ? (
          <KitButton iconLeft={RotateCw} onClick={onRetry} className="h-10 text-[13.5px] font-semibold">
            Try again
          </KitButton>
        ) : null}
        {actions}
      </Actions>
    </Frame>
  );
}

/**
 * A state's own action (library Empty state · Actions): a 40px secondary
 * capsule at 13.5/600, or, `quiet`, the accent words alone with no capsule.
 */
export function StateAction({
  children,
  onClick,
  quiet = false,
  primary = false,
  className,
}: {
  children: ReactNode;
  onClick: () => void;
  quiet?: boolean;
  /** The page's one way forward (Signals / No signals steering yet: See waiting). */
  primary?: boolean;
  className?: string;
}) {
  if (quiet) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "inline-flex h-10 items-center rounded-pill text-[13.5px] font-semibold text-co-accent-ink hover:underline hover:underline-offset-4",
          className,
        )}
      >
        {children}
      </button>
    );
  }
  return primary ? (
    <KitButton onClick={onClick} className={cn("h-10", className)}>
      {children}
    </KitButton>
  ) : (
    <KitButton variant="secondary" onClick={onClick} className={cn("h-10 text-[13.5px] font-semibold", className)}>
      {children}
    </KitButton>
  );
}

/**
 * Empty says why it is empty and offers the one action that fills it. `icon`
 * swaps the glyph in the box (a 20px icon, e.g. a lucide icon); pages that pass
 * none keep the default orbit.
 */
export function EmptyState({
  title,
  body,
  minHeight,
  align,
  className,
  icon,
  actions,
}: { title: string; body?: ReactNode; icon?: ReactNode; actions?: ReactNode } & Props) {
  return (
    <Frame minHeight={minHeight} align={align} className={className}>
      <GlyphBox>
        {icon ?? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <ellipse cx="12" cy="12" rx="10" ry="4.4" />
          </svg>
        )}
      </GlyphBox>
      <Title>{title}</Title>
      {body ? <Body>{body}</Body> : null}
      {actions ? <Actions>{actions}</Actions> : null}
    </Frame>
  );
}

/**
 * Empty state · With active filters: when the filters hide everything. It
 * names the active filters in a mono readout and offers Clear filters, and
 * Save where saving the search makes sense.
 */
export function FilteredEmptyState({
  title,
  body,
  activeFilters,
  onClear,
  onSave,
  minHeight,
  className,
}: {
  title: string;
  body: ReactNode;
  activeFilters: string[];
  onClear: () => void;
  onSave?: () => void;
  className?: string;
} & Props) {
  return (
    <div className={cn(className)}>
      <Frame minHeight={minHeight}>
        <GlyphBox>
          <SearchX aria-hidden />
        </GlyphBox>
        <Title>{title}</Title>
        <Body>{body}</Body>
        {activeFilters.length ? (
          <div className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-co-text2">
            Active: {activeFilters.join(" · ")}
          </div>
        ) : null}
        <Actions>
          <KitButton variant="secondary" onClick={onClear}>
            Clear filters
          </KitButton>
          {onSave ? <KitButton onClick={onSave}>Save</KitButton> : null}
        </Actions>
      </Frame>
    </div>
  );
}
